/**
 * Bland–Altman agreement statistics for two methods measured on the same subjects.
 *
 * Bland & Altman (1986, Lancet 327:307–310) plot each difference against the mean of the
 * pair and judge agreement by the interval mean ± 1.96 SD that should hold about 95% of the
 * differences. Bland & Altman (1999, Stat Methods Med Res 8:135–160) add the confidence
 * intervals used here:
 *
 * - bias:   d̄ ± t(n−1) · s/√n
 * - limits: L ± t(n−1) · s·√(1/n + 1.96²/(2(n−1)))   (an approximate variance)
 *
 * and, when the differences depend on the size of the measurement, regression-based limits
 * (their section 4): regress the differences on the means, regress the absolute residuals
 * on the means, and use  D̂(m) ± 1.96·√(π/2)·R̂(m), where 1.96·√(π/2) ≈ 2.46.
 *
 * Three scales: raw differences, percentage differences relative to the pair's mean, and
 * differences of natural logs (back-transformed to a ratio and ratio limits).
 */

import { studentTQuantile } from "@/lib/stats/distributions";
import {
  type LinearFit,
  linearFit,
} from "@/lib/tool-kit/stats-clinical/linear-fit";

export type AgreementScale = "difference" | "percent" | "ratio";

export const LIMIT_MULTIPLIER = 1.96;
const REGRESSION_LIMIT_MULTIPLIER = LIMIT_MULTIPLIER * Math.sqrt(Math.PI / 2);

export interface AgreementPair {
  a: number;
  b: number;
}

export interface PlotPoint {
  x: number;
  y: number;
}

export interface Interval {
  lower: number;
  upper: number;
}

export interface RegressionLimits {
  /** D̂(m) = intercept + slope·m, and the line of absolute residuals R̂(m). */
  mean: { intercept: number; slope: number };
  spread: { intercept: number; slope: number };
  /** True if R̂ stays above zero across the observed range. */
  positive: boolean;
}

export interface AgreementResult {
  scale: AgreementScale;
  n: number;
  points: PlotPoint[];
  bias: number;
  sd: number;
  biasCi: Interval;
  upper: number;
  lower: number;
  upperCi: Interval;
  lowerCi: Interval;
  /** Number of differences outside the limits. */
  outside: number;
  tCritical: number;
  /** Differences regressed on means: the proportional-bias check. */
  trend: LinearFit | null;
  regressionLimits: RegressionLimits | null;
  /** For the ratio scale: the same estimates after exponentiating. */
  ratio: {
    bias: number;
    upper: number;
    lower: number;
    biasCi: Interval;
    upperCi: Interval;
    lowerCi: Interval;
  } | null;
}

export type AgreementProblem = "few" | "nonPositive" | "zeroMean" | "noSpread";

function transformed(pair: AgreementPair, scale: AgreementScale): PlotPoint {
  const mean = (pair.a + pair.b) / 2;
  if (scale === "difference") {
    return { x: mean, y: pair.a - pair.b };
  }
  if (scale === "percent") {
    return { x: mean, y: (100 * (pair.a - pair.b)) / mean };
  }
  return { x: mean, y: Math.log(pair.a) - Math.log(pair.b) };
}

function intervalAround(value: number, half: number): Interval {
  return { lower: value - half, upper: value + half };
}

function expInterval(interval: Interval): Interval {
  return { lower: Math.exp(interval.lower), upper: Math.exp(interval.upper) };
}

function buildRegressionLimits(
  points: readonly PlotPoint[],
  trend: LinearFit | null
): RegressionLimits | null {
  if (!trend) {
    return null;
  }
  const xs = points.map((p) => p.x);
  const residuals = points.map((p) =>
    Math.abs(p.y - (trend.intercept + trend.slope * p.x))
  );
  const spread = linearFit(xs, residuals);
  if (!spread) {
    return null;
  }
  const lowest = spread.intercept + spread.slope * Math.min(...xs);
  const highest = spread.intercept + spread.slope * Math.max(...xs);
  return {
    mean: { intercept: trend.intercept, slope: trend.slope },
    spread: { intercept: spread.intercept, slope: spread.slope },
    positive: lowest > 0 && highest > 0,
  };
}

/** The value of the regression-based limits at a mean m: [lower, upper]. */
export function regressionLimitsAt(
  limits: RegressionLimits,
  m: number
): [number, number] {
  const centre = limits.mean.intercept + limits.mean.slope * m;
  const half =
    REGRESSION_LIMIT_MULTIPLIER *
    (limits.spread.intercept + limits.spread.slope * m);
  return [centre - half, centre + half];
}

export function checkPairs(
  pairs: readonly AgreementPair[],
  scale: AgreementScale
): AgreementProblem | null {
  if (pairs.length < 3) {
    return "few";
  }
  if (scale === "ratio" && pairs.some((p) => !(p.a > 0 && p.b > 0))) {
    return "nonPositive";
  }
  if (scale === "percent" && pairs.some((p) => p.a + p.b === 0)) {
    return "zeroMean";
  }
  const first = transformed(pairs[0], scale).y;
  if (pairs.every((p) => transformed(p, scale).y === first)) {
    return "noSpread";
  }
  return null;
}

export function agreement(
  pairs: readonly AgreementPair[],
  scale: AgreementScale = "difference"
): AgreementResult | null {
  if (checkPairs(pairs, scale) !== null) {
    return null;
  }
  const points = pairs.map((pair) => transformed(pair, scale));
  const n = points.length;
  const bias = points.reduce((s, p) => s + p.y, 0) / n;
  const variance = points.reduce((s, p) => s + (p.y - bias) ** 2, 0) / (n - 1);
  const sd = Math.sqrt(variance);
  const tCritical = studentTQuantile(0.975, n - 1);
  const upper = bias + LIMIT_MULTIPLIER * sd;
  const lower = bias - LIMIT_MULTIPLIER * sd;
  const limitSe = sd * Math.sqrt(1 / n + LIMIT_MULTIPLIER ** 2 / (2 * (n - 1)));
  const biasCi = intervalAround(bias, (tCritical * sd) / Math.sqrt(n));
  const upperCi = intervalAround(upper, tCritical * limitSe);
  const lowerCi = intervalAround(lower, tCritical * limitSe);
  const trend = linearFit(
    points.map((p) => p.x),
    points.map((p) => p.y)
  );
  return {
    scale,
    n,
    points,
    bias,
    sd,
    biasCi,
    upper,
    lower,
    upperCi,
    lowerCi,
    outside: points.filter((p) => p.y > upper || p.y < lower).length,
    tCritical,
    trend,
    regressionLimits: buildRegressionLimits(points, trend),
    ratio:
      scale === "ratio"
        ? {
            bias: Math.exp(bias),
            upper: Math.exp(upper),
            lower: Math.exp(lower),
            biasCi: expInterval(biasCi),
            upperCi: expInterval(upperCi),
            lowerCi: expInterval(lowerCi),
          }
        : null,
  };
}

export type AcceptabilityVerdict = "within" | "uncertain" | "outside";

/**
 * Bland & Altman (1999): agreement is acceptable if the upper limit's CI lies below the
 * largest acceptable difference and the lower limit's CI lies above its negative; it is
 * unacceptable if the limits themselves are outside them.
 */
export function judgeAgreement(
  result: AgreementResult,
  acceptable: number
): AcceptabilityVerdict {
  if (
    result.upperCi.upper <= acceptable &&
    result.lowerCi.lower >= -acceptable
  ) {
    return "within";
  }
  if (result.upper > acceptable || result.lower < -acceptable) {
    return "outside";
  }
  return "uncertain";
}
